// Dependencies
import { isAxiosError } from 'axios';

/**
 * Handle API errors and return an app-readable error code.
 */
export const handleApiError = (
  err: Error,
  options?: {
    setAttemptsRemaining?: (attempts: number) => void;
    setResetDate?: (resetDate: number) => void;
    onUnauthorized?: () => void;
    onConflict?: () => void;
    on?: (errorCode: string, details?: any) => void;
  }
): string => {
  /**
   * Declare the variable errorCode and assign it the value 'UNKNOWN_ERROR'.
   */
  let errorCode = err.message || 'UNKNOWN_ERROR';

  /**
   * Check if the object err is an Axios error object.
   */
  if (isAxiosError(err)) {
    /**
     * If the object contains a response
     */
    if (err.response) {
      /**
       * Extract headers and data from the response object.
       */
      const { headers, data } = err.response;

      /**
       * If the 'x-ratelimit-remaining' header exists and the setAttemptsRemaining function is passed,
       */
      if (headers['x-ratelimit-remaining'] && options?.setAttemptsRemaining) {
        options?.setAttemptsRemaining(Number(headers['x-ratelimit-remaining']));
      }

      /**
       * If the 'x-ratelimit-reset' header exists and the setResetDate function is passed,
       */
      if (headers['x-ratelimit-reset'] && options?.setResetDate) {
        options?.setResetDate(Number(headers['x-ratelimit-reset']));
      }

      /**
       * If the response code is 401 and the onUnauthorized function is passed,
       */
      if (data?.error?.code === 'UNAUTHORIZED' && options?.onUnauthorized) {
        options?.onUnauthorized();
      }

      /**
       * If the response code is 409 and the onConflict function is passed,
       */
      if (data?.error?.code === 'CONFLICT' && options?.onConflict) {
        options?.onConflict();
      }

      /**
       * If the on function is passed,
       */
      if (options?.on && data?.error?.code) {
        options?.on(data.error.code, data.error.details);
      }

      /**
       * Extract the error code from the data object.
       */
      errorCode = data?.error?.code || 'UNKNOWN_ERROR';
    } else if (err.request) {
      /**
       * If the object contains a request
       */
      errorCode = 'NETWORK';
    } else {
      /**
       * If the object contains neither a response nor a request
       */
      errorCode = 'UNKNOWN_ERROR';
    }
  }

  /**
   * Return the value of the errorCode variable.
   */
  return errorCode;
};
